/**
 * Deck bulk action bar - shown when cards are selected in selection mode.
 * Provides batch operations: move selected cards to a target zone, or remove
 * them all from the deck. Mirrors the collection BulkActionBar pattern.
 */
import type { ReactElement } from 'react';
import { IoArrowForward, IoCheckbox, IoClose, IoSquareOutline, IoTrash } from 'react-icons/io5';

import Button from '@/components/Button/Button';
import Dropdown, { type DropdownOption } from '@/components/Dropdown/Dropdown';
import { DECK_ZONE_LABELS } from '@/lib/deckUtils';
import type { DeckZone } from '@/types/deck';

export interface DeckBulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  /** Zones available as bulk-move targets. */
  availableZones: DeckZone[];
  /** Currently selected target zone. */
  targetZone: DeckZone;
  onTargetZoneChange: (zone: DeckZone) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onMove: () => void;
  onRemove: () => void;
  onClose: () => void;
  /** Whether a bulk operation is in progress. */
  isPending?: boolean;
}

export default function DeckBulkActionBar({
  selectedCount,
  totalCount,
  availableZones,
  targetZone,
  onTargetZoneChange,
  onSelectAll,
  onClearSelection,
  onMove,
  onRemove,
  onClose,
  isPending = false,
}: DeckBulkActionBarProps): ReactElement {
  const hasSelection = selectedCount > 0;
  const allSelected = selectedCount === totalCount && totalCount > 0;

  const zoneOptions: DropdownOption<DeckZone>[] = availableZones.map((zone) => ({
    value: zone,
    label: DECK_ZONE_LABELS[zone],
  }));

  return (
    <div className="bg-aether/10 border-aether/30 flex items-center gap-3 border-b px-4 py-2.5">
      <span className="text-pearl text-sm font-medium">{selectedCount.toLocaleString()} selected</span>

      <div className="bg-storm h-5 w-px" />

      {totalCount > 0 && (
        <button
          type="button"
          onClick={allSelected ? onClearSelection : onSelectAll}
          className="text-ash hover:text-pearl flex items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-sm transition-colors"
        >
          {allSelected ? <IoCheckbox className="h-4 w-4" /> : <IoSquareOutline className="h-4 w-4" />}
          Select all
        </button>
      )}

      <div className="bg-storm h-5 w-px" />

      {/* Move to zone */}
      <div className="flex items-center gap-2">
        <label htmlFor="bulk-move-zone" className="text-ash text-sm">
          Move to
        </label>
        <Dropdown<DeckZone>
          id="bulk-move-zone"
          value={targetZone}
          options={zoneOptions}
          onChange={(zone) => zone && onTargetZoneChange(zone)}
          disabled={isPending || availableZones.length === 0}
          aria-label="Target zone for bulk move"
        />
        <Button
          icon={<IoArrowForward />}
          text="Move"
          color="secondary"
          size="small"
          onClick={onMove}
          disabled={!hasSelection || isPending || availableZones.length === 0}
        />
      </div>

      <div className="bg-storm h-5 w-px" />

      <Button
        icon={<IoTrash />}
        text="Remove"
        color="delete"
        variant="outlined"
        size="small"
        onClick={onRemove}
        disabled={!hasSelection || isPending}
      />

      <div className="ml-auto">
        <button
          type="button"
          onClick={onClose}
          className="text-ash hover:text-pearl rounded-md p-1.5 transition-colors"
          aria-label="Exit selection mode"
        >
          <IoClose className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
